import WixSDK from 'shared/utils/wix-sdk';
import { createAction } from 'redux-actions';
import createAsyncActions from 'shared/redux/helpers/create-async-actions';
import wrapAsyncCall from 'shared/redux/helpers/wrap-async-call';
import { getCompId } from 'shared/utils/comp-id';
import trackError from 'shared/utils/track-error';
import {
  getTransactionErrorCode,
  getTransactionId,
  getTransactionStatus,
} from 'widget/selectors/query-params';
import * as cashier from 'api/payments/cashier';
import * as dgs from 'api/payments/dgs';
import { getVisitorId } from 'shared/bi/bi-vod';

export const STATUSES = {
  IDLE: 0,
  PENDING: 1,
  DONE: 2,
  FAIL: 3,
};

export const CREATE_ORDER = createAsyncActions('PAYMENT.CREATE_ORDER');

export const INIT_SDK = createAsyncActions('PAYMENT.INIT_SDK');

export const TRANSACTION_VERIFY = createAsyncActions(
  'PAYMENT.TRANSACTION_VERIFY',
);

export const INIT_CHECKOUT = createAsyncActions('PAYMENT.INIT_CHECKOUT');

export const SELECT_METHOD = createAction('PAYMENT.SELECT_METHOD');

export const statusChange = createAction('PAYMENT.STATUS_CHANGE');

export const setError = createAction('PAYMENT.ERROR');

export function createOrder(paymentType, channelId, videoId) {
  return dispatch => {
    dispatch(CREATE_ORDER.START({ paymentType, channelId, videoId }));
    return Promise.resolve(WixSDK.getSiteUrl())
      .then(siteUrl =>
        dgs.createOrder({
          paymentType,
          channelId,
          videoId,
          siteUrl,
          instance: WixSDK.Utils.getInstance(),
          compId: getCompId(),
        }),
      )
      .then(
        response => {
          dispatch(CREATE_ORDER.SUCCESS(response));
          return response;
        },
        error => {
          dispatch(CREATE_ORDER.FAIL(error));
          return Promise.reject(error);
        },
      );
  };
}

function getAppConfig(props = {}) {
  return {
    appDefId: WixSDK.Utils.getInstanceValue('appDefId'),
    instance: WixSDK.Utils.getInstance(),
    instanceId: WixSDK.Utils.getInstanceId(),
    locale: WixSDK.Utils.getLocale(),
    msid: WixSDK.Utils.getInstanceValue('metaSiteId'),
    visitorId: getVisitorId(),
    ...props,
  };
}

export const initSDK = wrapAsyncCall(
  INIT_SDK,
  cashier.configureSDK,
  ({ orderId }) => getAppConfig({ orderId }),
);

export const selectPaymentMethod = paymentMethod =>
  SELECT_METHOD(paymentMethod);

export function verifyTransaction() {
  function getStatus({
    transactionStatus,
    transactionId,
    errorCode,
    instance,
    ...config
  }) {
    if (transactionId) {
      return dgs.getPaymentStatus({ transactionId, instance });
    }
    if (errorCode) {
      return cashier
        .getPaymentsSDK()
        .then(paymentsSDK => paymentsSDK.configure({ instance, ...config }))
        .then(paymentsSDK => paymentsSDK.localizeError(errorCode))
        .then(error => ({ status: 'ERROR', error }));
    }
    return Promise.resolve({ status: transactionStatus || 'ERROR' });
  }

  return (dispatch, getState) => {
    const state = getState();
    const appConfig = getAppConfig({
      transactionStatus: getTransactionStatus(state),
      transactionId: getTransactionId(state),
      errorCode: getTransactionErrorCode(state),
    });
    dispatch(TRANSACTION_VERIFY.START(appConfig));
    return getStatus(appConfig).then(
      ({ status, error }) => {
        dispatch(TRANSACTION_VERIFY.SUCCESS({ status, error }));
        dispatch(
          !status || status === 'ERROR'
            ? setError(error || 'UNKNOWN_ERROR')
            : statusChange(status),
        );
      },
      error => {
        dispatch(TRANSACTION_VERIFY.FAIL(error));
        dispatch(setError(error || 'UNKNOWN_ERROR'));
        trackError(error);
      },
    );
  };
}

export const initPayment = ({
  paymentType,
  channelId,
  videoId,
}) => async dispatch => {
  try {
    dispatch(INIT_CHECKOUT.START());
    const response = await dispatch(
      createOrder(paymentType, channelId, videoId),
    );
    await dispatch(initSDK(response));
    dispatch(INIT_CHECKOUT.SUCCESS());
  } catch (error) {
    dispatch(INIT_CHECKOUT.FAIL(error));
    dispatch(setError(error));
  }
};
